<template>
  <section id="profileTools" v-if="employeeTools && employeeTools.length">
    <v-row class="mt-3" style="max-height: 56px">
      <v-col cols="auto">
        <span class="title text--secondary text-uppercase">{{
          $t("profile.subheader.tools")
        }}</span>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="employeeTools"
      class="safety-elevation-3 table-striped"
      hide-default-footer
      :items-per-page="-1"
      fixed-header
    >
      <template v-slot:[`item.groupId`]="{ item }">
        {{ item.group.name }}
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        {{ item.createdAt | date }}
      </template>

      <template v-slot:[`item.lastCheckedAt`]="{ item }">
        <span v-if="item.lastCheckedAt">
          {{ item.lastCheckedAt | date }}
        </span>
      </template>

      <template v-slot:[`item.calculatedNextCheckDays`]="{ item }">
        {{ calculateDaysUntilNextCheck(item) }}
      </template>

      <template v-slot:[`item.lastComment`]="{ item }">
        <span v-if="item.comments && item.comments.length">
          {{ truncateComment(item.comments[item.comments.length - 1].comment) }}
        </span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip small :color="getStatusColor(item.status)">
          <span :class="getStatusTextClass(item.status)">
            {{ getStatusText(item.status) }}
          </span>
        </v-chip>
      </template>

      <template v-slot:[`item.menu`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="openCommentsDialog(item)">
              {{ $t("tools.comment.button") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <tool-comments
      :show.sync="showCommentsDialog"
      :selectedTool="selectedTool"
      @refresh="$apollo.queries.employeeTools.refresh()"
    />
  </section>
</template>

<script>
import EmployeeToolsQuery from "@/graphql/queries/tools/EmployeeTools.gql";
import ToolComments from "@/components/tools/ToolComments.vue";

export default {
  components: { ToolComments },
  props: ["employee"],
  data() {
    return {
      showCommentsDialog: false,
      selectedTool: null,
    };
  },
  computed: {
    headers() {
      let h = [
        {
          text: this.$i18n.t("tools.column.id"),
          value: "engravedId",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.group"),
          value: "groupId",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.brand"),
          value: "brand",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.description"),
          value: "description",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.createdAt"),
          value: "createdAt",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.lastCheckedAt"),
          value: "lastCheckedAt",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.checkInDays"),
          value: "calculatedNextCheckDays",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.lastComment"),
          value: "lastComment",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("tools.column.status"),
          value: "status",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: "",
          value: "menu",
          sortable: false,
          align: "right",
        },
      ];

      return h;
    },
  },
  methods: {
    calculateDaysUntilNextCheck(item) {
      if (item.checkInDays == null) return null;
      const baseDate = item.lastCheckedAt
        ? new Date(item.lastCheckedAt)
        : new Date(item.createdAt);
      const nextCheckDate = new Date(baseDate);
      nextCheckDate.setDate(baseDate.getDate() + item.checkInDays);
      const now = new Date();
      const diffTime = nextCheckDate.getTime() - now.getTime();
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    },
    truncateComment(text) {
      return text.length > 35 ? text.slice(0, 35) + "..." : text;
    },
    getStatusColor(status) {
      switch (status) {
        case "ready":
          return "green lighten-4";
        case "inUse":
          return "orange lighten-4";
        case "sentToCheck":
          return "deep-orange lighten-4";
        case "recalled":
          return "deep-orange lighten-4";
        default:
          return "";
      }
    },

    getStatusText(status) {
      switch (status) {
        case "ready":
          return this.$i18n.t("tools.status.ready");
        case "inUse":
          return this.$i18n.t("tools.status.inUse");
        case "recalled":
          return this.$i18n.t("tools.status.recalled");
        case "sentToCheck":
          return this.$i18n.t("tools.status.sentToCheck");
        case "decommissioned":
          return this.$i18n.t("tools.status.decommissioned");
      }
    },
    getStatusTextClass(status) {
      switch (status) {
        case "ready":
          return "success--text text--darken-1 text-uppercase mr-1 font-weight-regular";
        case "inUse":
          return "warning--text text--darken-1 text-uppercase mr-1 font-weight-regular";
        case "sentToCheck":
          return "error--text text--darken-1 text-uppercase mr-1 font-weight-regular";
        case "recalled":
          return "error--text text--darken-1 text-uppercase mr-1 font-weight-regular";
        case "decommissioned":
          return "grey--text text--darken-1 text-uppercase mr-1 font-weight-regular";
        default:
          return "";
      }
    },
    openCommentsDialog(tool) {
      this.selectedTool = tool;
      this.showCommentsDialog = true;
    },
  },
  apollo: {
    employeeTools: {
      query: EmployeeToolsQuery,
      variables() {
        return {
          employeeId: this.employee.id,
        };
      },
      skip() {
        return !this.employee || !this.employee.id;
      },
    },
  },
};
</script>
